import { useState } from 'react';
import { FormattedMessage, defineMessages } from '@alltrails/shared/react-intl';
import Button from '@alltrails/shared/denali/components/Button';
import PlusBadge from '@alltrails/shared/denali/components/PlusBadge';
import Play from '@alltrails/shared/icons/Play';
import useToggle from '@alltrails/shared/hooks/useToggle';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import logFlyoverStartButtonTapped from '@alltrails/analytics/events/logFlyoverStartButtonTapped';
import CarouselDisplayTrigger from '@alltrails/analytics/enums/CarouselDisplayTrigger';
import FlyoverSource from '@alltrails/analytics/enums/FlyoverSource';
import SignupModal from 'components/SignupModal';
import useMobileWidth from 'hooks/useMobileWidth';
import useUser from 'hooks/useUser';
import FlyoverUpsell from 'components/FlyoverUpsell';
import { Trail } from 'types/Trails';
import User from '@alltrails/shared/types/User';
import * as styles from './styles.module.scss';

const messages = defineMessages({
  PREVIEW_TRAIL: {
    defaultMessage: 'Preview trail'
  }
});

type FlyoverEntryProps = {
  exploreFlag?: boolean;
  exactExplorePath: string;
  initializingFlyover?: boolean;
  onEntryClick?: () => void;
  trail: Trail;
};

const FlyoverEntry = ({ exploreFlag, exactExplorePath, initializingFlyover = false, onEntryClick, trail }: FlyoverEntryProps): JSX.Element => {
  const {
    formattedDefaultMessages: { PREVIEW_TRAIL }
  } = useFormatMessage(messages);

  const user = useUser();
  const isMobileWidth = useMobileWidth();

  const [isOpen, toggle] = useToggle(false);
  const [isFlyoverUpsellOpen, toggleFlyoverUpsell] = useToggle(false);
  const [showOfflineError, setShowOfflineError] = useState(false);

  const flyoverParam = 'initFlyover=true';
  const flyoverHref = exactExplorePath.indexOf('?') === -1 ? `${exactExplorePath}?${flyoverParam}` : `${exactExplorePath}&${flyoverParam}`;
  const linkInfo = !!user?.pro && !exploreFlag ? { href: flyoverHref } : null;

  const onFlyoverEntryClick = (ev: React.MouseEvent) => {
    logFlyoverStartButtonTapped({ source: exploreFlag ? FlyoverSource.MapDetails : FlyoverSource.TrailDetails, trail_id: trail.trail_id });
    setShowOfflineError(false);
    if (!window.navigator.onLine) {
      ev.preventDefault();
      setShowOfflineError(true);
      return;
    }

    if (!user) {
      toggle();
    } else if (!user.pro) {
      toggleFlyoverUpsell();
    } else if (user.pro) {
      onEntryClick?.();
    }
  };

  if (trail.length === 0 && trail.elevation_gain === 0) {
    return null;
  }

  return (
    <div className={styles.flyoverContainer}>
      <FlyoverUpsell flyoverHref={flyoverHref} isOpen={isFlyoverUpsellOpen} onRequestClose={toggleFlyoverUpsell} />
      <SignupModal
        trigger={CarouselDisplayTrigger.Flyover}
        isOpen={isOpen}
        onRequestClose={toggle}
        onSuccess={(user: User) => {
          toggle();

          if (!user.pro) {
            toggleFlyoverUpsell();
            return;
          }

          if (exploreFlag) {
            onEntryClick();
          } else {
            // redirect the user to explore so we can start the flyover
            window.location.assign(flyoverHref);
          }
        }}
      />
      <div className={styles.buttonEntry}>
        <Button
          icon={{ Component: Play }}
          loading={initializingFlyover}
          linkInfo={linkInfo}
          onClick={onFlyoverEntryClick}
          testId="flyover-entry"
          text={PREVIEW_TRAIL}
          size="md"
          width={isMobileWidth || exploreFlag ? 'full' : 'auto'}
        />
        <PlusBadge className={styles.plusBadge} />
      </div>
      {showOfflineError && (
        <span className={styles.error}>
          <FormattedMessage defaultMessage="We’re unable to  submit your request because you’re offline. Please try again later." />
        </span>
      )}
    </div>
  );
};

export default FlyoverEntry;
